import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Buildings, Clock, Users, ListChecks, Files, Stop, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import QuickStamp from "@/components/QuickStamp";
import GlobalTaskLogSearch from "@/components/GlobalTaskLogSearch";

function fmtSeconds(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // monday=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sumDurationFrom(reports, fromDate) {
  return reports
    .filter((r) => r.status === "completed" && new Date(r.start_time) >= fromDate)
    .reduce((s, r) => s + (r.duration_seconds || 0), 0);
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [reports, setReports] = useState([]);
  const [activeAll, setActiveAll] = useState([]);
  const [objects, setObjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [fileStats, setFileStats] = useState(null);
  const [forcing, setForcing] = useState({});

  const reloadActiveAll = async () => {
    try {
      const a = await api.get("/reports/active/all");
      setActiveAll(a.data);
    } catch (e) {
      console.error(e);
    }
  };

  const forceClockOut = async (row) => {
    if (!window.confirm(`Tvinga utstämpling för ${row.user_name}?\nRapporten markeras som tvingad och tiden räknas fram till nu.`)) {
      return;
    }
    try {
      setForcing((s) => ({ ...s, [row.report_id]: true }));
      await api.post(`/reports/${row.report_id}/force-clock-out`);
      toast.success(`${row.user_name} har stämplats ut.`);
      await reloadActiveAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte tvinga utstämpling");
    } finally {
      setForcing((s) => ({ ...s, [row.report_id]: false }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [r, o, u, fs] = await Promise.all([
          api.get("/reports"),
          api.get("/objects"),
          api.get("/users"),
          api.get("/objects/file-stats"),
        ]);
        setReports(r.data);
        setObjects(o.data);
        setUsers(u.data);
        setFileStats(fs.data);
        if (isAdmin) {
          const a = await api.get("/reports/active/all");
          setActiveAll(a.data);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isAdmin]);

  const now = new Date();
  const totalSecondsToday = sumDurationFrom(reports, startOfDay(now));
  const totalSecondsWeek = sumDurationFrom(reports, startOfWeek(now));
  const totalSecondsMonth = sumDurationFrom(reports, startOfMonth(now));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Dashboard</h1>
        <p className="text-text-muted mt-1">
          {isAdmin ? "Översikt över alla anställda och projekt." : "Din vecka i siffror."}
        </p>
      </header>

      {isAdmin && <OnboardingChecklist />}

      {!isAdmin && <QuickStamp />}

      {/* Sök i alla momentloggar — synligt för både admin och anställda */}
      <section className="tk-card p-5" data-testid="dashboard-tasklog-search-widget">
        <div className="flex items-center gap-2 mb-3">
          <MagnifyingGlass size={20} className="text-brand" />
          <div>
            <h2 className="font-heading text-xl leading-tight">Sök i momentloggen</h2>
            <p className="text-xs text-text-muted">
              Hitta gamla anteckningar och bilder från alla objekt — utan att behöva vara instämplad.
            </p>
          </div>
        </div>
        <GlobalTaskLogSearch testIdPrefix="dashboard-tasklog" compact />
      </section>

      <section
        className={
          isAdmin
            ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
            : "grid grid-cols-2 lg:grid-cols-4 gap-4"
        }
      >
        <KpiCard
          icon={Clock}
          label="Idag"
          value={fmtSeconds(totalSecondsToday)}
          testid="kpi-today"
        />
        <KpiCard
          icon={Clock}
          label="Vecka"
          value={fmtSeconds(totalSecondsWeek)}
          testid="kpi-week"
        />
        <KpiCard
          icon={Clock}
          label="Månad"
          value={fmtSeconds(totalSecondsMonth)}
          testid="kpi-month"
        />
        {!isAdmin && (
          <KpiCard
            icon={ListChecks}
            label="Mina rapporter"
            value={reports.length}
            testid="kpi-reports"
          />
        )}
        {isAdmin && (
          <>
            <KpiCard
              icon={Buildings}
              label="Aktiva objekt"
              value={objects.filter((o) => o.status === "active").length}
              testid="kpi-objects"
            />
            <KpiCard
              icon={Users}
              label="Instämplade nu"
              value={activeAll.length}
              testid="kpi-active-now"
            />
          </>
        )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="tk-card p-5 md:col-span-2 lg:col-span-3" data-testid="kpi-files">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted">
                Bifogade filer
              </div>
              <div className="font-heading text-3xl mt-2 tabular-nums">
                {fileStats ? `${fileStats.total} ${fileStats.total === 1 ? "fil" : "filer"} · ${fmtBytes(fileStats.bytes)} lagrat` : "—"}
              </div>
              {fileStats && fileStats.total > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <span
                    data-testid="kpi-files-pdf"
                    className="tk-pill bg-[var(--brand-rust)]/10 text-[var(--brand-rust)] border border-[var(--brand-rust)]/30"
                  >
                    {fileStats.pdf} PDF
                  </span>
                  <span
                    data-testid="kpi-files-xls"
                    className="tk-pill bg-[var(--brand-forest)]/10 text-[var(--brand-forest)] border border-[var(--brand-forest)]/30"
                  >
                    {fileStats.xls} Excel
                  </span>
                  <span
                    data-testid="kpi-files-dxf"
                    className="tk-pill bg-[var(--brand-sand)]/40 text-text-primary border border-app"
                  >
                    {fileStats.dxf} DXF
                  </span>
                </div>
              )}
            </div>
            <Files size={36} className="text-brand shrink-0" />
          </div>
        </div>
      </section>

      {isAdmin && (
        <section className="tk-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl">Just nu instämplade</h2>
            <span className="text-xs text-text-muted">{activeAll.length} personer</span>
          </div>
          {activeAll.length === 0 ? (
            <p className="text-text-muted text-sm">Ingen är instämplad just nu.</p>
          ) : (
            <div className="divide-y divide-[var(--border-c)]">
              {activeAll.map((a) => {
                const startMs = new Date(a.start_time).getTime();
                const hours = (Date.now() - startMs) / 3_600_000;
                const isLong = hours >= 10;
                return (
                  <div
                    key={a.report_id}
                    className="py-4 flex items-center justify-between gap-3 flex-wrap"
                    data-testid={`active-row-${a.report_id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-heading text-base flex items-center gap-2">
                        {a.user_name}
                        {isLong && (
                          <span
                            data-testid={`active-row-long-${a.report_id}`}
                            className="tk-pill bg-[var(--brand-rust)]/10 text-[var(--brand-rust)] border border-[var(--brand-rust)]/30 text-[10px]"
                          >
                            Över {Math.floor(hours)}h
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-text-muted">
                        {a.object_name} · {a.task_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm tabular-nums text-text-muted">
                        sedan {new Date(a.start_time).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm" })}
                      </div>
                      <button
                        data-testid={`force-clock-out-${a.report_id}`}
                        disabled={!!forcing[a.report_id]}
                        onClick={() => forceClockOut(a)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-[var(--brand-rust)] text-[var(--brand-rust)] hover:bg-[var(--brand-rust)] hover:text-white transition-colors disabled:opacity-50"
                        title="Tvinga utstämpling som admin"
                      >
                        <Stop size={12} weight="fill" />
                        {forcing[a.report_id] ? "Stämplar ut…" : "Tvinga ut"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="tk-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-text-muted">{label}</div>
        <Icon size={20} className="text-brand" />
      </div>
      <div className="font-heading text-3xl mt-3 tabular-nums">{value}</div>
    </div>
  );
}
